import { Droppable } from "@hello-pangea/dnd";
import { withErrorBoundary } from "@sentry/react";
import { defer } from "lodash";
import { useEffect, useRef } from "react";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import Error from "@components/error/error";
import MotionDiv from "@components/motion/motion-div";
import { destroyYDocAndWebsocketProvider } from "@components/wysiwyg/helpers";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import { MeetingWebsocketProviderContext } from "../context";
import useMeetingWebsocketProvider from "../use-meeting-websocket-provider";
import AddTopicHover from "./add-topic-hover";
import DragAndDrop from "./drag-and-drop";
import Topic, { defaultTopicTitle } from "./topic";

const Meeting = ({
  meeting,
  isInExtension = false, // true when this view appears in an extension
  opensTopicsInSidebar,
  currentPageMeeting = null,
  autoFocusDefaultTopic = true,
  animateTopicLayout = true,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  isInExtension?: boolean; // true when this view appears in an extension
  opensTopicsInSidebar: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
  // Autofocus the default topic's title input on mount. Must be disabled when
  // multiple meetings render at once (e.g. the profile 1-on-1 tab) — the focus
  // query is document-wide and would steal focus to another meeting, scrolling
  // the page.
  autoFocusDefaultTopic?: boolean;
  // Animate topic position changes (framer-motion layout). Must be disabled
  // when meetings stack in a paginated list (profile 1-on-1 tab, meeting-page
  // list): inserting a meeting above shifts every topic below, and the layout
  // animation makes them all slide to their new positions at once.
  animateTopicLayout?: boolean;
}) => {
  const meetingsGroupedById = {} as {
    [key: number]: MeetingViewMeetingNodeNewPageFragmentFragment;
  };
  meetingsGroupedById[meeting.id] = meeting;
  const topics = assertEdgesNonNull(meeting.topics);

  const meetingWebsocketContext = useMeetingWebsocketProvider(meeting);
  const meetingWebsocketContextRef = useRef(meetingWebsocketContext);
  meetingWebsocketContextRef.current = meetingWebsocketContext;

  useEffect(() => {
    return function cleanup() {
      // Destroy connections on unmount only. Running this whenever the
      // context object changes (which happens any time a peer toggles their
      // individual notes and flips participantIdsWithNotes) would tear down
      // every provider in the meeting, including the current user's own —
      // remounting their editor empty with a "Trying to connect" banner
      // (TF-1367).
      const websocketConfigs = Object.keys(meetingWebsocketContextRef.current);
      websocketConfigs.forEach((providerKey) => {
        destroyYDocAndWebsocketProvider(providerKey);
      });
    };
  }, []);

  // Focus on first title input if it's default one
  const hasOnlyOneDefaultTopics =
    topics.length === 1 && topics[0].title === defaultTopicTitle;
  useEffect(() => {
    if (autoFocusDefaultTopic && hasOnlyOneDefaultTopics) {
      const input = document.querySelector(
        '[aria-label="Topic title"]',
      ) as HTMLTextAreaElement;
      if (input) {
        defer(() => {
          input.focus();
        });
      }
    }
  }, [autoFocusDefaultTopic, hasOnlyOneDefaultTopics]);

  if (!meeting.meetingGroup) {
    return <Error title="This meeting does not exist." />;
  }

  return (
    <DragAndDrop meetingsGroupedById={meetingsGroupedById}>
      <div aria-label={`Meeting: ${meeting.title}`}>
        <div>
          <Droppable droppableId={`meeting-${meeting.id}`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {topics.length > 0 && (
                  <ul aria-label="Topic list" className="flex flex-col text-sm">
                    <MeetingWebsocketProviderContext.Provider
                      value={meetingWebsocketContext}
                    >
                      {topics.map((topic, index) => (
                        <MotionDiv
                          key={topic.clientId}
                          layout={animateTopicLayout ? "position" : false}
                          transition={{
                            layout: { duration: 0.15, ease: "easeInOut" },
                          }}
                          className={classNames(
                            isInExtension && "px-4",
                            "js-meeting-topic",
                          )}
                        >
                          {index === 0 && (
                            <AddTopicHover
                              meeting={meeting}
                              beforeTopicId={topic.id}
                              hideButton
                            />
                          )}
                          <Topic
                            index={index}
                            topic={topic}
                            meeting={meeting}
                            isInExtension={isInExtension}
                            opensInSidebar={opensTopicsInSidebar}
                            currentPageMeeting={currentPageMeeting}
                          />
                          <AddTopicHover
                            meeting={meeting}
                            afterTopicId={topic.id}
                          />
                        </MotionDiv>
                      ))}
                    </MeetingWebsocketProviderContext.Provider>
                    {provided.placeholder}
                  </ul>
                )}
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </DragAndDrop>
  );
};

const MeetingWithSentry = withErrorBoundary(Meeting, {
  fallback: <Error description={"The meeting could not be rendered."} />,
});

export default MeetingWithSentry;
